// ═══════════════════════════════════════════════════════════════════════════
// FINANCIAL DASHBOARD TAB - UrbanQ v1.9.6
// Panel con KPIs, donut de gastos y comparativa mensual
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import {
    FiTrendingUp, FiTrendingDown, FiDollarSign, FiTarget,
    FiPieChart, FiCalendar, FiAlertTriangle, FiRefreshCw,
    FiDownload, FiCheckCircle, FiX
} from 'react-icons/fi';
import {
    LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend
} from 'recharts';
import { exportToCSV, exportToPDF, formatMonthlyForExport } from './FinancialExport';

export default function FinancialDashboardTab({ year, month, token, apiUrl, scope }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [expandedCard, setExpandedCard] = useState(null);
    const [cardDetailData, setCardDetailData] = useState(null);
    const [cardDetailLoading, setCardDetailLoading] = useState(false);

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(
                `${apiUrl}/api/financial/dashboard?year=${year}&month=${month}&scope=${scope}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const result = await response.json();
            if (result.success) {
                setData(result.data);
            } else {
                setError(result.message || 'Error al cargar dashboard');
            }
        } catch (err) {
            console.error('Error fetching dashboard:', err);
            setError('Error de conexion');
        } finally {
            setLoading(false);
        }
    }, [year, month, token, apiUrl, scope]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    // Cerrar card expandida al cambiar de mes/año
    useEffect(() => {
        setExpandedCard(null);
        setCardDetailData(null);
    }, [year, month]);

    const formatCurrency = (amount) => {
        return `$${parseFloat(amount || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
    };

    // formatPercent: para ratios (0.33 → "33.0%")
    const formatPercent = (value) => {
        return `${(parseFloat(value || 0) * 100).toFixed(1)}%`;
    };

    // Generar gradient para donut chart
    // percentage viene como string "33.3" (ya es %) del backend
    const generateDonutGradient = (categories) => {
        if (!categories || categories.length === 0) {
            return 'conic-gradient(#e2e8f0 0deg 360deg)';
        }

        let currentAngle = 0;
        const segments = categories.map(cat => {
            const pctValue = parseFloat(cat.percentage) / 100; // convertir "33.3" → 0.333
            const angle = pctValue * 360;
            const segment = `${cat.color} ${currentAngle}deg ${currentAngle + angle}deg`;
            currentAngle += angle;
            return segment;
        });

        return `conic-gradient(${segments.join(', ')})`;
    };

    // Fetch detalle on-demand al expandir card
    const fetchCardDetail = useCallback(async (cardType) => {
        try {
            setCardDetailLoading(true);
            const headers = { 'Authorization': `Bearer ${token}` };
            let url = '';

            if (cardType === 'income') {
                url = `${apiUrl}/api/financial/income?year=${year}&month=${month}&scope=${scope}`;
            } else if (cardType === 'expense' || cardType === 'fixed') {
                url = `${apiUrl}/api/financial/expense?year=${year}&month=${month}&scope=${scope}`;
            }

            if (url) {
                const res = await fetch(url, { headers });
                const result = await res.json();
                if (result.success) {
                    let items = result.data || [];
                    // Para gastos fijos, filtrar solo los de tipo 'Fijo'
                    if (cardType === 'fixed') {
                        items = items.filter(e => e.expense_type === 'Fijo');
                    }
                    setCardDetailData(items.slice(0, 8));
                }
            } else if (cardType === 'balance') {
                // Balance no necesita fetch adicional
                setCardDetailData('balance');
            }
        } catch (err) {
            console.error('Error fetching card detail:', err);
            setCardDetailData(null);
        } finally {
            setCardDetailLoading(false);
        }
    }, [token, apiUrl, year, month, scope]);

    const handleCardClick = (cardType) => {
        if (expandedCard === cardType) {
            setExpandedCard(null);
            setCardDetailData(null);
        } else {
            setExpandedCard(cardType);
            fetchCardDetail(cardType);
        }
    };

    // Tooltip personalizado para el gráfico de líneas
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="urbanq-finanzas-chart-tooltip">
                    <p className="urbanq-finanzas-chart-tooltip-label">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ color: entry.color }}>
                            {entry.name}: {formatCurrency(entry.value)}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="urbanq-finanzas-loading">
                <div className="urbanq-finanzas-spinner"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="urbanq-finanzas-empty">
                <FiAlertTriangle className="urbanq-finanzas-empty-icon" />
                <h3>Error al cargar datos</h3>
                <p>{error}</p>
                <button className="urbanq-finanzas-btn primary" onClick={fetchDashboard}>
                    <FiRefreshCw /> Reintentar
                </button>
            </div>
        );
    }

    const { yearIndicators, monthIndicators, expensesByCategory, monthlyComparison, budgetAlerts } = data || {};

    // Filtrar alertas válidas: solo con presupuesto definido y porcentaje >= 80%
    const validAlerts = (budgetAlerts || []).filter(a => {
        const budget = parseFloat(a.budget_amount);
        const pct = parseFloat(a.percentage);
        return budget > 0 && pct >= 80;
    });

    // Renderizar detalle expandido de una card
    const renderCardDetail = () => {
        if (cardDetailLoading) {
            return (
                <div className="urbanq-finanzas-card-detail urbanq-finanzas-card-detail-enter">
                    <div className="urbanq-finanzas-loading" style={{ padding: '20px 0' }}>
                        <div className="urbanq-finanzas-spinner"></div>
                    </div>
                </div>
            );
        }

        if (!cardDetailData) return null;

        if (cardDetailData === 'balance') {
            const mi = monthIndicators || {};
            return (
                <div className="urbanq-finanzas-card-detail urbanq-finanzas-card-detail-enter">
                    <div className="urbanq-finanzas-card-detail-header">
                        <span>Resumen del Balance</span>
                        <button className="urbanq-finanzas-btn-icon" onClick={() => { setExpandedCard(null); setCardDetailData(null); }}>
                            <FiX />
                        </button>
                    </div>
                    <table>
                        <tbody>
                            <tr>
                                <td>Ingresos del Mes</td>
                                <td className="numeric amount positive">{formatCurrency(mi.totalIncome)}</td>
                            </tr>
                            <tr>
                                <td>Gastos del Mes</td>
                                <td className="numeric amount negative">{formatCurrency(mi.totalExpense)}</td>
                            </tr>
                            <tr>
                                <td><strong>Balance</strong></td>
                                <td className={`numeric amount ${(mi.balance || 0) >= 0 ? 'positive' : 'negative'}`}>
                                    <strong>{formatCurrency(mi.balance)}</strong>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        }

        if (Array.isArray(cardDetailData) && cardDetailData.length > 0) {
            return (
                <div className="urbanq-finanzas-card-detail urbanq-finanzas-card-detail-enter">
                    <div className="urbanq-finanzas-card-detail-header">
                        <span>
                            {expandedCard === 'income' ? 'Ingresos Recientes' :
                             expandedCard === 'fixed' ? 'Gastos Fijos del Mes' : 'Gastos Recientes'}
                        </span>
                        <button className="urbanq-finanzas-btn-icon" onClick={() => { setExpandedCard(null); setCardDetailData(null); }}>
                            <FiX />
                        </button>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Descripción</th>
                                <th>Categoría</th>
                                <th className="numeric">Monto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cardDetailData.map((item, i) => (
                                <tr key={i}>
                                    <td>{item.date ? new Date(item.date).toLocaleDateString('es-EC', { day: '2-digit', month: 'short' }) : '-'}</td>
                                    <td>{item.description || '-'}</td>
                                    <td>{item.category_name || '-'}</td>
                                    <td className={`numeric amount ${expandedCard === 'income' ? 'positive' : 'negative'}`}>
                                        {formatCurrency(item.amount)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }

        return (
            <div className="urbanq-finanzas-card-detail urbanq-finanzas-card-detail-enter">
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '16px' }}>
                    No hay registros para este mes
                </p>
            </div>
        );
    };

    return (
        <div className="urbanq-finanzas-dashboard-tab" id="financial-dashboard-content">
            {/* Indicadores del Año con Gráfico de Líneas */}
            <div className="urbanq-finanzas-section">
                <div className="urbanq-finanzas-section-header">
                    <h2><FiCalendar /> Indicadores del Año {year}</h2>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <div className="urbanq-finanzas-export-buttons">
                            <button
                                className="urbanq-finanzas-btn secondary sm"
                                onClick={() => {
                                    if (monthlyComparison) {
                                        const formattedData = formatMonthlyForExport(monthlyComparison);
                                        exportToCSV(formattedData, `resumen_financiero_${year}`, ['Mes', 'Ingresos', 'Gastos', 'Balance', 'Tasa Ahorro']);
                                    }
                                }}
                                title="Exportar a Excel"
                            >
                                <FiDownload /> Excel
                            </button>
                            <button
                                className="urbanq-finanzas-btn secondary sm"
                                onClick={() => exportToPDF('financial-dashboard-content', `Resumen Financiero ${year}`, `resumen_${year}`)}
                                title="Exportar a PDF"
                            >
                                <FiDownload /> PDF
                            </button>
                        </div>
                        <button className="urbanq-finanzas-btn-icon" onClick={fetchDashboard}>
                            <FiRefreshCw />
                        </button>
                    </div>
                </div>

                <div className="urbanq-finanzas-annual-layout">
                    {/* KPIs compactos a la izquierda */}
                    <div className="urbanq-finanzas-annual-kpi-compact">
                        <div className="urbanq-finanzas-annual-kpi-item">
                            <FiTrendingUp style={{ color: '#10b981', flexShrink: 0 }} />
                            <div>
                                <div className="value" style={{ color: '#10b981' }}>{formatCurrency(yearIndicators?.totalIncome)}</div>
                                <div className="label">Ingresos Totales</div>
                            </div>
                        </div>
                        <div className="urbanq-finanzas-annual-kpi-item">
                            <FiTrendingDown style={{ color: '#ef4444', flexShrink: 0 }} />
                            <div>
                                <div className="value" style={{ color: '#ef4444' }}>{formatCurrency(yearIndicators?.totalExpense)}</div>
                                <div className="label">Gastos Totales</div>
                            </div>
                        </div>
                        <div className="urbanq-finanzas-annual-kpi-item">
                            <FiDollarSign style={{ color: (yearIndicators?.balance || 0) >= 0 ? '#10b981' : '#ef4444', flexShrink: 0 }} />
                            <div>
                                <div className="value" style={{ color: (yearIndicators?.balance || 0) >= 0 ? '#10b981' : '#ef4444' }}>
                                    {formatCurrency(yearIndicators?.balance)}
                                </div>
                                <div className="label">Balance Anual</div>
                            </div>
                        </div>
                        <div className="urbanq-finanzas-annual-kpi-item">
                            <FiTarget style={{ color: '#6366f1', flexShrink: 0 }} />
                            <div>
                                <div className="value" style={{ color: '#6366f1' }}>{formatPercent(yearIndicators?.savingsRate)}</div>
                                <div className="label">Tasa de Ahorro</div>
                            </div>
                        </div>
                    </div>

                    {/* Gráfico de Líneas a la derecha */}
                    <div className="urbanq-finanzas-annual-chart-container">
                        {monthlyComparison && monthlyComparison.length > 0 ? (
                            <ResponsiveContainer width="100%" height={220}>
                                <LineChart data={monthlyComparison}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(99, 102, 241, 0.1)" />
                                    <XAxis
                                        dataKey="name"
                                        stroke="#94a3b8"
                                        fontSize={11}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="#94a3b8"
                                        fontSize={10}
                                        tickLine={false}
                                        tickFormatter={(v) => v > 0 ? `$${(v / 1000).toFixed(0)}k` : '$0'}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="income"
                                        name="Ingresos"
                                        stroke="#10b981"
                                        strokeWidth={2.5}
                                        dot={{ fill: '#10b981', r: 3 }}
                                        activeDot={{ r: 5, fill: '#10b981', stroke: '#10b981', strokeWidth: 2 }}
                                        style={{ filter: 'drop-shadow(0 0 6px rgba(16, 185, 129, 0.5))' }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="expense"
                                        name="Gastos"
                                        stroke="#ef4444"
                                        strokeWidth={2.5}
                                        dot={{ fill: '#ef4444', r: 3 }}
                                        activeDot={{ r: 5, fill: '#ef4444', stroke: '#ef4444', strokeWidth: 2 }}
                                        style={{ filter: 'drop-shadow(0 0 6px rgba(239, 68, 68, 0.5))' }}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="savings"
                                        name="Balance"
                                        stroke="#6366f1"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={{ fill: '#6366f1', r: 2 }}
                                        activeDot={{ r: 4, fill: '#6366f1' }}
                                        style={{ filter: 'drop-shadow(0 0 6px rgba(99, 102, 241, 0.5))' }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '220px', color: '#94a3b8' }}>
                                Sin datos para graficar
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* KPIs del Mes — Cards clicables */}
            <div className="urbanq-finanzas-section">
                <h2><FiPieChart /> {data?.monthName || 'Mes'} {year}</h2>

                <div className="urbanq-finanzas-kpi-grid">
                    <div
                        className={`urbanq-finanzas-kpi-card urbanq-finanzas-month-card green ${expandedCard === 'income' ? 'expanded' : ''}`}
                        onClick={() => handleCardClick('income')}
                    >
                        <div className="urbanq-finanzas-kpi-icon">
                            <FiTrendingUp />
                        </div>
                        <div className="urbanq-finanzas-kpi-content">
                            <span className="urbanq-finanzas-kpi-value">
                                {formatCurrency(monthIndicators?.totalIncome)}
                            </span>
                            <span className="urbanq-finanzas-kpi-label">Ingresos del Mes</span>
                        </div>
                    </div>

                    <div
                        className={`urbanq-finanzas-kpi-card urbanq-finanzas-month-card red ${expandedCard === 'expense' ? 'expanded' : ''}`}
                        onClick={() => handleCardClick('expense')}
                    >
                        <div className="urbanq-finanzas-kpi-icon">
                            <FiTrendingDown />
                        </div>
                        <div className="urbanq-finanzas-kpi-content">
                            <span className="urbanq-finanzas-kpi-value">
                                {formatCurrency(monthIndicators?.totalExpense)}
                            </span>
                            <span className="urbanq-finanzas-kpi-label">Gastos del Mes</span>
                        </div>
                    </div>

                    <div
                        className={`urbanq-finanzas-kpi-card urbanq-finanzas-month-card blue ${expandedCard === 'balance' ? 'expanded' : ''}`}
                        onClick={() => handleCardClick('balance')}
                    >
                        <div className="urbanq-finanzas-kpi-icon">
                            <FiDollarSign />
                        </div>
                        <div className="urbanq-finanzas-kpi-content">
                            <span className="urbanq-finanzas-kpi-value" style={{
                                color: (monthIndicators?.balance || 0) >= 0 ? '#48bb78' : '#e53e3e'
                            }}>
                                {formatCurrency(monthIndicators?.balance)}
                            </span>
                            <span className="urbanq-finanzas-kpi-label">Balance Mensual</span>
                        </div>
                    </div>

                    <div
                        className={`urbanq-finanzas-kpi-card urbanq-finanzas-month-card orange ${expandedCard === 'fixed' ? 'expanded' : ''}`}
                        onClick={() => handleCardClick('fixed')}
                    >
                        <div className="urbanq-finanzas-kpi-icon">
                            <FiTarget />
                        </div>
                        <div className="urbanq-finanzas-kpi-content">
                            <span className="urbanq-finanzas-kpi-value">
                                {formatCurrency(monthIndicators?.fixedExpenses)}
                            </span>
                            <span className="urbanq-finanzas-kpi-label">Gastos Fijos</span>
                        </div>
                    </div>
                </div>

                {/* Panel de detalle expandible */}
                {expandedCard && renderCardDetail()}
            </div>

            {/* Donut Chart + Tabla Comparativa */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '25px' }}>
                {/* Donut de Gastos por Categoría */}
                <div className="urbanq-finanzas-section">
                    <h2><FiPieChart /> Distribución de Gastos</h2>

                    {expensesByCategory && expensesByCategory.length > 0 ? (
                        <div className="urbanq-finanzas-donut-container">
                            <div
                                className="urbanq-finanzas-donut"
                                style={{ background: generateDonutGradient(expensesByCategory) }}
                            >
                                <div className="urbanq-finanzas-donut-center">
                                    <span className="urbanq-finanzas-donut-total">
                                        {formatCurrency(yearIndicators?.totalExpense)}
                                    </span>
                                    <span className="urbanq-finanzas-donut-label">Total Gastos</span>
                                </div>
                            </div>

                            <div className="urbanq-finanzas-donut-legend">
                                {expensesByCategory.slice(0, 6).map((cat, index) => (
                                    <div key={index} className="urbanq-finanzas-legend-item">
                                        <div
                                            className="urbanq-finanzas-legend-color"
                                            style={{ background: cat.color }}
                                        ></div>
                                        <div className="urbanq-finanzas-legend-info">
                                            <span className="urbanq-finanzas-legend-name">{cat.category}</span>
                                            <span>
                                                <span className="urbanq-finanzas-legend-amount">
                                                    {formatCurrency(cat.total)}
                                                </span>
                                                <span className="urbanq-finanzas-legend-percent">
                                                    {parseFloat(cat.percentage).toFixed(1)}%
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="urbanq-finanzas-empty" style={{ padding: '40px' }}>
                            <FiPieChart style={{ fontSize: '48px', color: '#e2e8f0' }} />
                            <p style={{ marginTop: '15px', color: '#718096' }}>
                                No hay gastos registrados
                            </p>
                        </div>
                    )}

                    {/* Area Chart — Flujo de Caja Mensual */}
                    {monthlyComparison && monthlyComparison.length > 0 && (
                        <div className="urbanq-finanzas-cashflow-mini">
                            <h4>Flujo de Caja Mensual</h4>
                            <ResponsiveContainer width="100%" height={200}>
                                <AreaChart data={monthlyComparison}>
                                    <defs>
                                        <linearGradient id="cashflowIncome" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="cashflowExpense" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.1)" />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        tick={{ fill: '#94a3b8', fontSize: 10 }}
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={(v) => `$${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`}
                                        width={50}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            background: 'rgba(15, 23, 42, 0.95)',
                                            border: '1px solid rgba(99, 102, 241, 0.3)',
                                            borderRadius: '8px',
                                            color: '#e2e8f0',
                                            fontSize: '12px'
                                        }}
                                        formatter={(value, name) => {
                                            const labels = { income: 'Ingresos', expense: 'Gastos' };
                                            return [`$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, labels[name] || name];
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="income"
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        fill="url(#cashflowIncome)"
                                        dot={false}
                                        activeDot={{ r: 4, stroke: '#10b981', strokeWidth: 2, fill: '#0f172a' }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="expense"
                                        stroke="#ef4444"
                                        strokeWidth={2}
                                        fill="url(#cashflowExpense)"
                                        dot={false}
                                        activeDot={{ r: 4, stroke: '#ef4444', strokeWidth: 2, fill: '#0f172a' }}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                            <div className="urbanq-finanzas-cashflow-mini-legend">
                                <span><span style={{ background: '#10b981' }} className="urbanq-finanzas-legend-dot"></span> Ingresos</span>
                                <span><span style={{ background: '#ef4444' }} className="urbanq-finanzas-legend-dot"></span> Gastos</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabla Comparativa Mensual */}
                <div className="urbanq-finanzas-section">
                    <h2><FiCalendar /> Comparativa Mensual</h2>

                    <div className="urbanq-finanzas-table-wrapper">
                        <table className="urbanq-finanzas-table grid-lines">
                            <thead>
                                <tr>
                                    <th>Mes</th>
                                    <th className="numeric">Ingresos</th>
                                    <th className="numeric">Gastos</th>
                                    <th className="numeric">Ahorro</th>
                                    <th className="numeric">%</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyComparison?.map((m, index) => (
                                    <tr key={index}>
                                        <td>{m.name}</td>
                                        <td className="numeric amount positive">
                                            {formatCurrency(m.income)}
                                        </td>
                                        <td className="numeric amount negative">
                                            {formatCurrency(m.expense)}
                                        </td>
                                        <td className={`numeric amount ${m.savings >= 0 ? 'positive' : 'negative'}`}>
                                            {formatCurrency(m.savings)}
                                        </td>
                                        <td className="numeric">
                                            {formatPercent(m.savingsRate)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Gráfico de Flujo de Caja */}
            <div className="urbanq-finanzas-section">
                <h2><FiTrendingUp /> Flujo de Caja Mensual</h2>

                {monthlyComparison && monthlyComparison.length > 0 ? (
                    <div className="urbanq-finanzas-cashflow-chart">
                        {/* Leyenda */}
                        <div className="urbanq-finanzas-cashflow-legend">
                            <span className="urbanq-finanzas-cashflow-legend-item income">
                                <span className="legend-dot"></span> Ingresos
                            </span>
                            <span className="urbanq-finanzas-cashflow-legend-item expense">
                                <span className="legend-dot"></span> Gastos
                            </span>
                            <span className="urbanq-finanzas-cashflow-legend-item savings">
                                <span className="legend-dot"></span> Balance
                            </span>
                        </div>

                        {/* Barras */}
                        <div className="urbanq-finanzas-cashflow-bars">
                            {(() => {
                                const maxValue = Math.max(
                                    ...monthlyComparison.map(m => Math.max(m.income || 0, m.expense || 0))
                                );
                                const scale = maxValue > 0 ? 100 / maxValue : 0;

                                return monthlyComparison.map((m, index) => (
                                    <div key={index} className="urbanq-finanzas-cashflow-bar-group">
                                        <div className="urbanq-finanzas-cashflow-bar-container">
                                            <div
                                                className="urbanq-finanzas-cashflow-bar income"
                                                style={{ height: `${(m.income || 0) * scale}%` }}
                                                title={`Ingresos: ${formatCurrency(m.income)}`}
                                            >
                                                {m.income > maxValue * 0.15 && (
                                                    <span className="bar-value">{formatCurrency(m.income)}</span>
                                                )}
                                            </div>
                                            <div
                                                className="urbanq-finanzas-cashflow-bar expense"
                                                style={{ height: `${(m.expense || 0) * scale}%` }}
                                                title={`Gastos: ${formatCurrency(m.expense)}`}
                                            >
                                                {m.expense > maxValue * 0.15 && (
                                                    <span className="bar-value">{formatCurrency(m.expense)}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className={`urbanq-finanzas-cashflow-balance ${m.savings >= 0 ? 'positive' : 'negative'}`}>
                                            {m.savings >= 0 ? '+' : ''}{formatCurrency(m.savings)}
                                        </div>
                                        <span className="urbanq-finanzas-cashflow-month">{m.name?.substring(0, 3)}</span>
                                    </div>
                                ));
                            })()}
                        </div>
                    </div>
                ) : (
                    <div className="urbanq-finanzas-empty" style={{ padding: '40px' }}>
                        <FiTrendingUp style={{ fontSize: '48px', color: '#e2e8f0' }} />
                        <p style={{ marginTop: '15px', color: '#718096' }}>
                            No hay datos de flujo de caja
                        </p>
                    </div>
                )}
            </div>

            {/* Alertas de Presupuesto */}
            <div className="urbanq-finanzas-section">
                <h2><FiAlertTriangle /> Alertas de Presupuesto</h2>
                {validAlerts.length > 0 ? (
                    <div className="urbanq-finanzas-alerts">
                        {validAlerts.map((cat, index) => {
                            const pct = parseFloat(cat.percentage);
                            return (
                                <div
                                    key={index}
                                    className={`urbanq-finanzas-alert ${pct >= 100 ? 'danger' : 'warning'}`}
                                >
                                    <FiAlertTriangle />
                                    <span>
                                        <strong>{cat.category}</strong>: Has usado el {pct.toFixed(1)}% de tu presupuesto
                                        ({formatCurrency(cat.spent)} de {formatCurrency(cat.budget_amount)})
                                        {pct >= 100 ? ' — Excedido!' : ' — Cerca del límite'}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="urbanq-finanzas-alert-ok">
                        <FiCheckCircle />
                        <span>Todo en orden — No hay alertas de presupuesto</span>
                    </div>
                )}
            </div>
        </div>
    );
}
